<script setup>
/**
 * Proxy Customer tool: embed the real website widget as a visitor.
 * Identity is controlled via cw_conversation JWT — no shared browser cookie.
 */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import {
  createProxyCustomer,
  listProxyCustomers,
  renameProxyCustomer,
} from '../api';

const { t } = useI18n();
const store = useStore();

const accountId = computed(
  () =>
    store.getters.getCurrentAccountId || store.state.route?.params?.accountId
);

const loading = ref(true);
const creating = ref(false);
const renaming = ref(false);
const errorMsg = ref('');
const sessions = ref([]);
const inboxes = ref([]);
const selectedContactId = ref(null);
const selectedInboxId = ref('');
const draftName = ref('');
const iframeKey = ref(0);
const widgetFrame = ref(null);

const activeSession = computed(() =>
  sessions.value.find(s => s.contact?.id === selectedContactId.value)
);

const widgetSrc = computed(() => {
  const path = activeSession.value?.widget_path;
  if (!path) return '';
  // Same-origin widget; cache-bust on switch so iframe remounts cleanly.
  return `${path}&_=${iframeKey.value}`;
});

const needsInboxPick = computed(() => inboxes.value.length > 1);

const debugLog = (msg, extra) => {
  // eslint-disable-next-line no-console
  console.debug(`[proxy_customer] ${msg}`, extra ?? '');
};

// Inside an iframe the widget stays idle until its host answers the `loaded`
// event with `config-set`; without that handshake it never loads history.
// The SDK normally plays that host role — here this page does.
const WIDGET_EVENT_PREFIX = 'chatwoot-widget:';
let themeObserver;

const dashboardColorScheme = () => {
  const selectedScheme =
    LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'dark';
  if (selectedScheme === 'light') return 'light';
  if (selectedScheme === 'dark') return 'dark';
  return document.body.classList.contains('dark') ? 'dark' : 'light';
};

const postToWidget = (event, payload = {}) => {
  const frameWindow = widgetFrame.value?.contentWindow;
  if (!frameWindow) return;
  frameWindow.postMessage(
    `${WIDGET_EVENT_PREFIX}${JSON.stringify({ event, ...payload })}`,
    window.location.origin
  );
};

const onWidgetMessage = event => {
  if (event.source !== widgetFrame.value?.contentWindow) return;
  if (typeof event.data !== 'string') return;
  if (!event.data.startsWith(WIDGET_EVENT_PREFIX)) return;

  let message;
  try {
    message = JSON.parse(event.data.slice(WIDGET_EVENT_PREFIX.length));
  } catch {
    return;
  }
  if (message.event !== 'loaded') return;

  const channelConfig = message.config?.channelConfig || {};
  postToWidget('config-set', {
    locale: channelConfig.locale,
    position: 'right',
    hideMessageBubble: true,
    showPopoutButton: false,
    widgetStyle: 'rounded',
    darkMode: dashboardColorScheme(),
    showUnreadMessagesDialog: false,
  });
  // Land straight on the conversation instead of the widget home screen.
  postToWidget('toggle-open', { isOpen: true });
  debugLog('widget handshake', { contactId: selectedContactId.value });
};

const selectSession = session => {
  if (!session?.contact?.id) return;
  selectedContactId.value = session.contact.id;
  draftName.value = session.contact.name || '';
  iframeKey.value += 1;
  debugLog('switch session', { contactId: session.contact.id });
};

const load = async () => {
  loading.value = true;
  errorMsg.value = '';
  try {
    const data = await listProxyCustomers(accountId.value);
    sessions.value = data.payload || [];
    inboxes.value = data.inboxes || [];
    if (inboxes.value.length === 1) {
      selectedInboxId.value = String(inboxes.value[0].id);
    }
    if (
      selectedContactId.value &&
      !sessions.value.some(s => s.contact?.id === selectedContactId.value)
    ) {
      selectedContactId.value = null;
    }
    if (!selectedContactId.value && sessions.value.length) {
      selectSession(sessions.value[0]);
    }
    debugLog('loaded', {
      sessions: sessions.value.length,
      inboxes: inboxes.value.length,
    });
  } catch (err) {
    const code = err?.response?.data?.error;
    if (code === 'feature_disabled') {
      errorMsg.value = t('CUSTOM.PROXY_CUSTOMER.FEATURE_OFF');
    } else {
      errorMsg.value =
        err?.response?.data?.error || t('CUSTOM.COMMON.LOAD_FAILED');
    }
    debugLog('load failed', code || err?.message);
  } finally {
    loading.value = false;
  }
};

const onCreate = async () => {
  if (!inboxes.value.length) {
    errorMsg.value = t('CUSTOM.PROXY_CUSTOMER.NO_INBOX');
    return;
  }
  if (needsInboxPick.value && !selectedInboxId.value) {
    errorMsg.value = t('CUSTOM.PROXY_CUSTOMER.PICK_INBOX');
    return;
  }

  creating.value = true;
  errorMsg.value = '';
  try {
    const payload = {};
    if (selectedInboxId.value) payload.inbox_id = Number(selectedInboxId.value);
    const session = await createProxyCustomer(accountId.value, payload);
    sessions.value = [session, ...sessions.value];
    selectSession(session);
    await nextTick();
    debugLog('created', { contactId: session.contact?.id });
  } catch (err) {
    const code = err?.response?.data?.error;
    if (code === 'inbox_required') {
      errorMsg.value = t('CUSTOM.PROXY_CUSTOMER.PICK_INBOX');
    } else if (code === 'no_website_inbox' || code === 'inbox_not_found') {
      errorMsg.value = t('CUSTOM.PROXY_CUSTOMER.NO_INBOX');
    } else {
      errorMsg.value = code || t('CUSTOM.PROXY_CUSTOMER.CREATE_FAILED');
    }
    debugLog('create failed', code || err?.message);
  } finally {
    creating.value = false;
  }
};

const onRename = async () => {
  if (!selectedContactId.value) return;
  const name = draftName.value.trim();
  if (!name) {
    errorMsg.value = t('CUSTOM.PROXY_CUSTOMER.NAME_REQUIRED');
    return;
  }
  renaming.value = true;
  errorMsg.value = '';
  try {
    const data = await renameProxyCustomer(
      accountId.value,
      selectedContactId.value,
      name
    );
    sessions.value = sessions.value.map(s =>
      s.contact?.id === data.id
        ? { ...s, contact: { ...s.contact, name: data.name } }
        : s
    );
    draftName.value = data.name;
    debugLog('renamed', { contactId: data.id });
  } catch (err) {
    errorMsg.value =
      err?.response?.data?.error || t('CUSTOM.COMMON.SAVE_FAILED');
  } finally {
    renaming.value = false;
  }
};

watch(selectedContactId, id => {
  const session = sessions.value.find(s => s.contact?.id === id);
  if (session) draftName.value = session.contact?.name || '';
});

onMounted(() => {
  window.addEventListener('message', onWidgetMessage);
  themeObserver = new MutationObserver(() => {
    postToWidget('set-color-scheme', {
      darkMode: dashboardColorScheme(),
    });
  });
  themeObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
  load();
});

onBeforeUnmount(() => {
  window.removeEventListener('message', onWidgetMessage);
  themeObserver?.disconnect();
});
</script>

<template>
  <div
    class="flex flex-1 flex-col w-full min-w-0 h-full min-h-0 bg-n-surface-1"
  >
    <header class="px-6 lg:px-8 py-5 border-b border-n-weak shrink-0">
      <h1 class="text-xl font-semibold tracking-tight text-n-slate-12">
        {{ t('CUSTOM.PROXY_CUSTOMER.TITLE') }}
      </h1>
      <p class="mt-1.5 text-sm leading-5 text-n-slate-11">
        {{ t('CUSTOM.PROXY_CUSTOMER.SUBTITLE') }}
      </p>
    </header>

    <div
      v-if="loading"
      class="flex flex-1 items-center justify-center text-sm text-n-slate-11"
    >
      {{ t('CUSTOM.COMMON.LOADING') }}
    </div>

    <div
      v-else
      class="flex flex-1 min-w-0 min-h-0 gap-4 p-4 lg:gap-5 lg:p-5 overflow-hidden"
    >
      <!-- Visitor list -->
      <aside
        class="flex flex-col w-72 xl:w-80 shrink-0 overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
      >
        <div class="p-3.5 space-y-3 border-b border-n-weak">
          <div v-if="needsInboxPick" class="space-y-1">
            <label class="text-xs text-n-slate-11">
              {{ t('CUSTOM.PROXY_CUSTOMER.INBOX') }}
            </label>
            <select
              v-model="selectedInboxId"
              class="w-full h-9 rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            >
              <option value="" disabled>
                {{ t('CUSTOM.PROXY_CUSTOMER.PICK_INBOX') }}
              </option>
              <option
                v-for="inbox in inboxes"
                :key="inbox.id"
                :value="String(inbox.id)"
              >
                {{ inbox.name }}
              </option>
            </select>
          </div>
          <button
            type="button"
            class="w-full h-9 rounded-lg bg-n-brand px-3 text-sm font-medium text-white transition-transform active:scale-[0.98] disabled:opacity-50"
            :disabled="creating || !inboxes.length"
            @click="onCreate"
          >
            {{
              creating
                ? t('CUSTOM.PROXY_CUSTOMER.CREATING')
                : t('CUSTOM.PROXY_CUSTOMER.CREATE')
            }}
          </button>
        </div>

        <p v-if="!sessions.length" class="p-4 text-sm text-n-slate-11">
          {{ t('CUSTOM.PROXY_CUSTOMER.EMPTY') }}
        </p>

        <ul class="flex-1 overflow-y-auto">
          <li v-for="session in sessions" :key="session.contact.id">
            <button
              type="button"
              class="w-full text-left px-3.5 py-3 border-b border-n-weak hover:bg-n-alpha-1 transition-colors"
              :class="{
                'bg-n-alpha-2 border-l-2 border-l-n-brand':
                  selectedContactId === session.contact.id,
              }"
              @click="selectSession(session)"
            >
              <div class="text-sm font-medium text-n-slate-12 truncate">
                {{ session.contact.name || t('CUSTOM.PROXY_CUSTOMER.UNNAMED') }}
              </div>
              <div class="text-xs text-n-slate-11 truncate mt-0.5">
                {{ session.inbox_name }}
              </div>
            </button>
          </li>
        </ul>
      </aside>

      <!-- Widget pane -->
      <section
        class="flex flex-1 flex-col min-w-0 min-h-0 overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
      >
        <p v-if="errorMsg" class="mx-4 mt-3 text-sm text-n-ruby-11">
          {{ errorMsg }}
        </p>

        <div
          v-if="activeSession"
          class="flex items-center gap-2.5 px-4 py-3 border-b border-n-weak bg-n-solid-1 shrink-0"
        >
          <input
            v-model="draftName"
            type="text"
            class="flex-1 h-9 rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            :placeholder="t('CUSTOM.PROXY_CUSTOMER.NAME_PLACEHOLDER')"
            @keydown.enter.prevent="onRename"
          />
          <button
            type="button"
            class="h-9 rounded-lg border border-n-weak bg-n-solid-2 px-4 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 active:scale-[0.98] disabled:opacity-50"
            :disabled="renaming"
            @click="onRename"
          >
            {{
              renaming
                ? t('CUSTOM.COMMON.SAVING')
                : t('CUSTOM.PROXY_CUSTOMER.RENAME')
            }}
          </button>
        </div>

        <div
          v-if="!activeSession"
          class="flex flex-1 items-center justify-center text-sm text-n-slate-11 px-6 text-center"
        >
          {{ t('CUSTOM.PROXY_CUSTOMER.SELECT_HINT') }}
        </div>

        <iframe
          v-else-if="widgetSrc"
          :key="iframeKey"
          ref="widgetFrame"
          :src="widgetSrc"
          class="flex-1 w-full min-w-0 border-0 bg-n-solid-2"
          :title="t('CUSTOM.PROXY_CUSTOMER.TITLE')"
          allow="microphone; clipboard-write"
        />
      </section>
    </div>
  </div>
</template>
